import { useMemo } from 'react';
import { useAppSelector } from 'src/stateManagement/hooks/redux';
import {
    useGetRepairPlansQuery,
    useGetRepairSegmentsLengthQuery,
} from 'src/api/rtk/base/slices/objectTypes/repairPlanApiSlice';
import { appliedFiltersToArrayParams } from '../../filters/utils/appliedFiltersToParams';
import css from './RepairPlanLength.module.scss';
import SmallLoader from 'src/widgets/loaders/SmallLoader';
import { skipToken } from '@reduxjs/toolkit/query';

const SCHEMA = [
    { field: 'RepairTypeStandartWorkType', param: 'StandartWorkTypes' },
    { field: 'RaionId', param: 'Raions' },
] as const;

const nf = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export default function RepairPlanLength() {
    const appliedFilters = useAppSelector((s) => s.maps?.repairPlan?.appliedFilters);

    const {
        data: repairPlans,
        isFetching: isPlansFetching,
        isUninitialized: isPlansUninitialized,
        isError: isPlansError,
    } = useGetRepairPlansQuery({ $orderby: 'Year desc', $filter: '(Accepted eq 1)' });

    const repairPlanId = useMemo<number | undefined>(
        () => repairPlans?.value?.[0]?.RepairPlanID,
        [repairPlans]
    );

    const { params, hardEmpty } = useMemo(
        () => appliedFiltersToArrayParams(appliedFilters, SCHEMA),
        [appliedFilters]
    );

    const shouldSkip = !repairPlanId || hardEmpty;

    type SegmentsArgs = Parameters<typeof useGetRepairSegmentsLengthQuery>[0];
    const queryArgs: SegmentsArgs | typeof skipToken = shouldSkip
        ? skipToken
        : ({ RepairPlanId: repairPlanId!, ...params } as const);

    const {
        data,
        isFetching,
        isLoading,
        isUninitialized,
        isError,
    } = useGetRepairSegmentsLengthQuery(queryArgs);

    const loading =
        (!repairPlanId && (isPlansUninitialized || isPlansFetching)) ||
        (!shouldSkip && (isLoading || isFetching || isUninitialized));

    const error = isPlansError || isError;
    const value = hardEmpty ? 0 : (data !== undefined ? data?.Length : null);

    const content = loading
        ? (<SmallLoader text="Загрузка…" iconColor="black" aria-live="polite" role="status" />)
        : (error
            ? 'Ошибка'
            : (value !== null
                ? `${nf.format(value)} км`
                : '—'));

    return (
        <div className={css.root}>
            <span><b>Протяжённость:</b></span>
            {content}
        </div>
    );
}
